import { type FinancialStatementType } from '../../../types/reports';
import { useCurrency } from '../../../hooks/useCurrency';
import { CurrencyCode } from '../../../config/currency';

interface FinancialStatementPreviewProps {
  type: FinancialStatementType;
  data: any;
  currency?: CurrencyCode;
}

export function FinancialStatementPreview({ type, data, currency }: FinancialStatementPreviewProps) {
  const { format } = useCurrency();
  
  // Formater un montant avec gestion des valeurs manquantes
  const formatAmount = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null || isNaN(amount)) return 'N/A';
    return format(amount, currency);
  };

  // Obtenir un tableau avec fallback vide
  const getArray = (obj: any, key: string): any[] => {
    const val = obj?.[key];
    return Array.isArray(val) ? val : [];
  };

  // Obtenir un objet avec fallback
  const getObject = (obj: any, key: string): any => {
    return obj?.[key] || {};
  };

  if (!data) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucune donnée disponible
      </div>
    );
  }

  const renderBalanceSheet = () => {
    const fixedAssets = getObject(data, 'fixedAssets');
    const currentAssets = getObject(data, 'currentAssets');
    const treasuryAssets = getObject(data, 'treasuryAssets');
    const equity = getObject(data, 'equity');
    const financialDebts = getObject(data, 'financialDebts');
    const currentLiabilities = getObject(data, 'currentLiabilities');
    const totalAssets = getObject(data, 'totalAssets');
    const totalLiabilities = getObject(data, 'totalLiabilities');

    // Fonction pour rendre une ligne de tableau
    const renderRow = (item: any, key: string | number) => (
      <tr key={key}>
        <td className="px-4 py-2 border-r border-gray-200">{item?.code || 'N/A'}</td>
        <td className="px-4 py-2 border-r border-gray-200">{item?.label || 'N/A'}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(item?.brut)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(item?.amort)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(item?.net)}</td>
        <td className="px-4 py-2 text-right">{formatAmount(item?.netN1)}</td>
      </tr>
    );

    // Fonction pour rendre un total
    const renderTotal = (label: string, total: any, className: string = 'bg-gray-100 font-medium') => (
      <tr className={className}>
        <td colSpan={2} className="px-4 py-2 border-r border-gray-200">{label}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(total?.brut)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(total?.amort)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(total?.net)}</td>
        <td className="px-4 py-2 text-right">{formatAmount(total?.netN1)}</td>
      </tr>
    );

    // Fonction pour rendre une ligne passif (4 colonnes)
    const renderPassifRow = (item: any, key: string | number) => (
      <tr key={key}>
        <td className="px-4 py-2 border-r border-gray-200">{item?.code || 'N/A'}</td>
        <td className="px-4 py-2 border-r border-gray-200">{item?.label || 'N/A'}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(item?.net)}</td>
        <td className="px-4 py-2 text-right">{formatAmount(item?.netN1)}</td>
      </tr>
    );

    const renderPassifTotal = (label: string, total: any, className: string = 'bg-gray-100 font-medium') => (
      <tr className={className}>
        <td colSpan={2} className="px-4 py-2 border-r border-gray-200">{label}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(total?.net)}</td>
        <td className="px-4 py-2 text-right">{formatAmount(total?.netN1)}</td>
      </tr>
    );

    return (
      <div className="space-y-8">
        {/* ACTIF */}
        <div>
          <h3 className="text-lg font-bold mb-4 print:text-black">ACTIF</h3>
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 print:border-gray-300">
            <thead className="bg-muted print:bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Code</th>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Libellé</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Brut</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Amort/Prov</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Net</th>
                <th className="px-4 py-2 text-right border-b border-gray-200">Net N-1</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Actif immobilisé */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={6} className="px-4 py-2">Actif immobilisé</td>
              </tr>
              {getArray(fixedAssets, 'intangibleAssets').length > 0 ? (
                getArray(fixedAssets, 'intangibleAssets').map((item: any, idx: number) => renderRow(item, `intang-${idx}`))
              ) : (
                <tr><td colSpan={6} className="px-4 py-2 text-gray-400 italic">Aucune immobilisation incorporelle</td></tr>
              )}
              {getArray(fixedAssets, 'tangibleAssets').map((item: any, idx: number) => renderRow(item, `tang-${idx}`))}
              {getArray(fixedAssets, 'financialAssets').map((item: any, idx: number) => renderRow(item, `fin-${idx}`))}
              {renderTotal('Total Actif immobilisé', fixedAssets.total)}

              {/* Actif circulant */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={6} className="px-4 py-2">Actif circulant</td>
              </tr>
              {getArray(currentAssets, 'inventory').length > 0 ? (
                getArray(currentAssets, 'inventory').map((item: any, idx: number) => renderRow(item, `inv-${idx}`))
              ) : (
                <tr><td colSpan={6} className="px-4 py-2 text-gray-400 italic">Aucun stock</td></tr>
              )}
              {getArray(currentAssets, 'receivables').map((item: any, idx: number) => renderRow(item, `rec-${idx}`))}
              {getArray(currentAssets, 'other').map((item: any, idx: number) => renderRow(item, `other-${idx}`))}
              {renderTotal('Total Actif circulant', currentAssets.total)}

              {/* Trésorerie Actif */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={6} className="px-4 py-2">Trésorerie Actif</td>
              </tr>
              {getArray(treasuryAssets, 'banks').map((item: any, idx: number) => renderRow(item, `bank-${idx}`))}
              {getArray(treasuryAssets, 'cashOnHand').map((item: any, idx: number) => renderRow(item, `cash-${idx}`))}
              {renderTotal('Total Trésorerie Actif', treasuryAssets.total)}

              {/* Total Actif */}
              {renderTotal('TOTAL ACTIF', totalAssets, 'bg-primary print:bg-gray-700 text-white print:text-white font-bold')}
            </tbody>
          </table>
        </div>

        {/* PASSIF */}
        <div>
          <h3 className="text-lg font-bold mb-4 print:text-black">PASSIF</h3>
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200 print:border-gray-300">
            <thead className="bg-muted print:bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Code</th>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Libellé</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Net</th>
                <th className="px-4 py-2 text-right border-b border-gray-200">Net N-1</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {/* Capitaux propres */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={4} className="px-4 py-2">Capitaux propres</td>
              </tr>
              {getArray(equity, 'capital').length > 0 ? (
                getArray(equity, 'capital').map((item: any, idx: number) => renderPassifRow(item, `cap-${idx}`))
              ) : (
                <tr><td colSpan={4} className="px-4 py-2 text-gray-400 italic">Aucun capital</td></tr>
              )}
              {getArray(equity, 'reserves').map((item: any, idx: number) => renderPassifRow(item, `res-${idx}`))}
              {getArray(equity, 'result').map((item: any, idx: number) => renderPassifRow(item, `result-${idx}`))}
              {renderPassifTotal('Total Capitaux propres', equity.total)}

              {/* Dettes financières */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={4} className="px-4 py-2">Dettes financières</td>
              </tr>
              {getArray(financialDebts, 'loans').map((item: any, idx: number) => renderPassifRow(item, `loan-${idx}`))}
              {getArray(financialDebts, 'leasing').map((item: any, idx: number) => renderPassifRow(item, `lease-${idx}`))}
              {renderPassifTotal('Total Dettes financières', financialDebts.total)}

              {/* Dettes circulantes */}
              <tr className="bg-gray-50 font-medium">
                <td colSpan={4} className="px-4 py-2">Passif circulant</td>
              </tr>
              {getArray(currentLiabilities, 'suppliers').map((item: any, idx: number) => renderPassifRow(item, `supp-${idx}`))}
              {getArray(currentLiabilities, 'fiscal').map((item: any, idx: number) => renderPassifRow(item, `fisc-${idx}`))}
              {getArray(currentLiabilities, 'social').map((item: any, idx: number) => renderPassifRow(item, `soc-${idx}`))}
              {renderPassifTotal('Total Passif circulant', currentLiabilities.total)}

              {/* Total Passif */}
              {renderPassifTotal('TOTAL PASSIF', totalLiabilities, 'bg-primary print:bg-gray-700 text-white print:text-white font-bold')}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderIncomeStatement = () => {
    const operatingIncome = getObject(data, 'operatingIncome');
    const operatingExpenses = getObject(data, 'operatingExpenses');
    const operatingResult = getObject(data, 'operatingResult');
    const financialResult = getObject(data, 'financialResult');
    const netResult = getObject(data, 'netResult');

    const renderIncomeRow = (item: any, key: string | number) => (
      <tr key={key}>
        <td className="px-4 py-2 border-r border-gray-200">{item?.code || 'N/A'}</td>
        <td className="px-4 py-2 border-r border-gray-200">{item?.label || 'N/A'}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(item?.current)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(item?.previous)}</td>
        <td className="px-4 py-2 text-right">{item?.variation !== undefined ? `${item.variation}%` : 'N/A'}</td>
      </tr>
    );

    const renderIncomeTotal = (label: string, total: any, className: string = 'bg-gray-100 font-medium') => (
      <tr className={className}>
        <td colSpan={2} className="px-4 py-2 border-r border-gray-200">{label}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(total?.current)}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(total?.previous)}</td>
        <td className="px-4 py-2 text-right">{total?.variation !== undefined ? `${total.variation}%` : 'N/A'}</td>
      </tr>
    );

    return (
      <div className="space-y-8">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Code</th>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Libellé</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">N</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">N-1</th>
              <th className="px-4 py-2 text-right border-b border-gray-200">Variation</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {/* Produits d'exploitation */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={5} className="px-4 py-2">Produits d'exploitation</td>
            </tr>
            {getArray(operatingIncome, 'sales').length > 0 ? (
              getArray(operatingIncome, 'sales').map((item: any, idx: number) => renderIncomeRow(item, `sales-${idx}`))
            ) : (
              <tr><td colSpan={5} className="px-4 py-2 text-gray-400 italic">Aucune vente enregistrée</td></tr>
            )}
            {getArray(operatingIncome, 'production').map((item: any, idx: number) => renderIncomeRow(item, `prod-${idx}`))}
            {getArray(operatingIncome, 'other').map((item: any, idx: number) => renderIncomeRow(item, `other-inc-${idx}`))}
            {renderIncomeTotal('Total Produits d\'exploitation', operatingIncome.total)}

            {/* Charges d'exploitation */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={5} className="px-4 py-2">Charges d'exploitation</td>
            </tr>
            {getArray(operatingExpenses, 'purchases').map((item: any, idx: number) => renderIncomeRow(item, `purch-${idx}`))}
            {getArray(operatingExpenses, 'services').map((item: any, idx: number) => renderIncomeRow(item, `serv-${idx}`))}
            {getArray(operatingExpenses, 'personnel').map((item: any, idx: number) => renderIncomeRow(item, `pers-${idx}`))}
            {getArray(operatingExpenses, 'depreciation').map((item: any, idx: number) => renderIncomeRow(item, `depr-${idx}`))}
            {getArray(operatingExpenses, 'other').map((item: any, idx: number) => renderIncomeRow(item, `other-exp-${idx}`))}
            {renderIncomeTotal('Total Charges d\'exploitation', operatingExpenses.total)}

            {/* Résultat d'exploitation */}
            {renderIncomeTotal('RÉSULTAT D\'EXPLOITATION', operatingResult, 'bg-blue-100 font-bold')}

            {/* Résultat financier */}
            {renderIncomeTotal('Résultat financier', financialResult)}

            {/* Résultat net */}
            {renderIncomeTotal('RÉSULTAT NET', netResult, 'bg-primary print:bg-gray-700 text-white print:text-white font-bold')}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCashFlow = () => {
    const operatingActivities = getObject(data, 'operatingActivities');
    const investingActivities = getObject(data, 'investingActivities');
    const financingActivities = getObject(data, 'financingActivities');
    const netCashChange = getObject(data, 'netCashChange');
    const openingCash = getObject(data, 'openingCash');
    const closingCash = getObject(data, 'closingCash');

    // Gérer le cas où les données peuvent avoir 'amount' au lieu de 'current'
    const getAmount = (item: any) => item?.current ?? item?.amount ?? null;
    const getPrevious = (item: any) => item?.previous ?? null;

    const renderCashRow = (item: any, key: string | number) => (
      <tr key={key}>
        <td className="px-4 py-2 border-r border-gray-200">{item?.code || 'N/A'}</td>
        <td className="px-4 py-2 border-r border-gray-200">{item?.label || 'N/A'}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(getAmount(item))}</td>
        <td className="px-4 py-2 text-right">{formatAmount(getPrevious(item))}</td>
      </tr>
    );

    const renderCashTotal = (label: string, total: any, className: string = 'bg-gray-100 font-medium') => (
      <tr className={className}>
        <td colSpan={2} className="px-4 py-2 border-r border-gray-200">{label}</td>
        <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(getAmount(total))}</td>
        <td className="px-4 py-2 text-right">{formatAmount(getPrevious(total))}</td>
      </tr>
    );

    return (
      <div className="space-y-8">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Code</th>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Libellé</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">N</th>
              <th className="px-4 py-2 text-right border-b border-gray-200">N-1</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {/* Flux d'exploitation */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={4} className="px-4 py-2">Flux de trésorerie liés à l'exploitation</td>
            </tr>
            {renderCashRow(operatingActivities.netResult, 'net-result')}
            {getArray(operatingActivities, 'adjustments').map((item: any, idx: number) => renderCashRow(item, `adj-${idx}`))}
            {getArray(operatingActivities, 'workingCapital').map((item: any, idx: number) => renderCashRow(item, `wc-${idx}`))}
            {renderCashTotal('Total Flux d\'exploitation', operatingActivities.total)}

            {/* Flux d'investissement */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={4} className="px-4 py-2">Flux de trésorerie liés à l'investissement</td>
            </tr>
            {getArray(investingActivities, 'acquisitions').map((item: any, idx: number) => renderCashRow(item, `acq-${idx}`))}
            {getArray(investingActivities, 'disposals').map((item: any, idx: number) => renderCashRow(item, `disp-${idx}`))}
            {renderCashTotal('Total Flux d\'investissement', investingActivities.total)}

            {/* Flux de financement */}
            <tr className="bg-gray-50 font-medium">
              <td colSpan={4} className="px-4 py-2">Flux de trésorerie liés au financement</td>
            </tr>
            {getArray(financingActivities, 'capital').map((item: any, idx: number) => renderCashRow(item, `fcap-${idx}`))}
            {getArray(financingActivities, 'loans').map((item: any, idx: number) => renderCashRow(item, `floan-${idx}`))}
            {getArray(financingActivities, 'repayments').map((item: any, idx: number) => renderCashRow(item, `frep-${idx}`))}
            {getArray(financingActivities, 'dividends').map((item: any, idx: number) => renderCashRow(item, `fdiv-${idx}`))}
            {renderCashTotal('Total Flux de financement', financingActivities.total)}

            {/* Variation de trésorerie */}
            {renderCashTotal('VARIATION DE TRÉSORERIE', netCashChange, 'bg-primary print:bg-gray-700 text-white print:text-white font-bold')}

            {/* Trésorerie d'ouverture et de clôture */}
            {renderCashTotal('Trésorerie d\'ouverture', openingCash)}
            {renderCashTotal('Trésorerie de clôture', closingCash, 'bg-green-100 font-bold')}
          </tbody>
        </table>
      </div>
    );
  };

  const renderEquityChanges = () => {
    // Si les données sont au format backend non adapté (equityComponents)
    if (data.equityComponents) {
      const components = Array.isArray(data.equityComponents) ? data.equityComponents : [];
      const totalEquity = data.totalEquity || {};

      return (
        <div className="space-y-8">
          <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-2 text-left border-b border-r border-gray-200">Composante</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Solde d'ouverture</th>
                <th className="px-4 py-2 text-right border-b border-r border-gray-200">Mouvements</th>
                <th className="px-4 py-2 text-right border-b border-gray-200">Solde de clôture</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {components.length > 0 ? (
                components.map((comp: any, idx: number) => (
                  <tr key={idx}>
                    <td className="px-4 py-2 border-r border-gray-200">{comp?.label || 'N/A'}</td>
                    <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(comp?.openingBalance)}</td>
                    <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(comp?.changes)}</td>
                    <td className="px-4 py-2 text-right">{formatAmount(comp?.closingBalance)}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={4} className="px-4 py-2 text-gray-400 italic text-center">Aucune composante de capitaux propres</td></tr>
              )}
              <tr className="bg-primary text-white font-bold">
                <td className="px-4 py-2 border-r border-gray-200">TOTAL CAPITAUX PROPRES</td>
                <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(totalEquity?.openingBalance)}</td>
                <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(totalEquity?.changes)}</td>
                <td className="px-4 py-2 text-right">{formatAmount(totalEquity?.closingBalance)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    // Format adapté avec net/netN1/variation
    const capital = getArray(data, 'capital');
    const premiums = getArray(data, 'premiums');
    const legalReserves = getArray(data, 'legalReserves');
    const otherReserves = getArray(data, 'otherReserves');
    const retainedEarnings = getArray(data, 'retainedEarnings');
    const netResult = getArray(data, 'netResult');
    const total = getObject(data, 'total');

    const allItems = [...capital, ...premiums, ...legalReserves, ...otherReserves, ...retainedEarnings, ...netResult];

    return (
      <div className="space-y-8">
        <table className="min-w-full divide-y divide-gray-200 border border-gray-200">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-2 text-left border-b border-r border-gray-200">Composante</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">N-1</th>
              <th className="px-4 py-2 text-right border-b border-r border-gray-200">Variation</th>
              <th className="px-4 py-2 text-right border-b border-gray-200">N</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {allItems.length > 0 ? (
              allItems.map((item: any, idx: number) => (
                <tr key={idx}>
                  <td className="px-4 py-2 border-r border-gray-200">{item?.label || 'N/A'}</td>
                  <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(item?.netN1)}</td>
                  <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(item?.variation)}</td>
                  <td className="px-4 py-2 text-right">{formatAmount(item?.net)}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={4} className="px-4 py-2 text-gray-400 italic text-center">Aucune composante de capitaux propres</td></tr>
            )}
            <tr className="bg-primary text-white font-bold">
              <td className="px-4 py-2 border-r border-gray-200">{total?.label || 'TOTAL CAPITAUX PROPRES'}</td>
              <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(total?.netN1)}</td>
              <td className="px-4 py-2 text-right border-r border-gray-200">{formatAmount(total?.variation)}</td>
              <td className="px-4 py-2 text-right">{formatAmount(total?.net)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderNotes = () => {
    return (
      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-bold mb-2">Notes annexes</h4>
          {data.notes ? (
            <div className="prose max-w-none">
              {typeof data.notes === 'string' ? (
                <p>{data.notes}</p>
              ) : Array.isArray(data.notes) ? (
                data.notes.map((note: any, idx: number) => (
                  <div key={idx} className="mb-4">
                    <h5 className="font-medium">{note.title || `Note ${idx + 1}`}</h5>
                    <p>{note.content || 'N/A'}</p>
                  </div>
                ))
              ) : (
                <pre className="text-sm overflow-auto">{JSON.stringify(data, null, 2)}</pre>
              )}
            </div>
          ) : (
            <p className="text-gray-500 italic">Aucune note disponible</p>
          )}
        </div>
      </div>
    );
  };

  const renderGenericReport = () => {
    return (
      <div className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-bold mb-2">Données du rapport ({type})</h4>
          <pre className="text-sm overflow-auto bg-white p-4 rounded border">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  switch (type) {
    case 'balance':
      return renderBalanceSheet();
    case 'income':
      return renderIncomeStatement();
    case 'cashflow':
      return renderCashFlow();
    case 'equity-changes':
      return renderEquityChanges();
    case 'notes':
      return renderNotes();
    default:
      return renderGenericReport();
  }
}